import type { RichTextElement, SharedDataQuerySpec } from 'coop-adventure-engine';

export type SharedVariablesQueryResults = {
  results: Record<string, string | number>;
  content?: RichTextElement[];
};

function buildCacheKey(
  sessionId: string,
  queries: SharedDataQuerySpec[],
  content?: RichTextElement[]
): string {
  const normalized = [...queries].sort((a, b) => {
    const left = `${a.variableName}:${a.aggregation}`;
    const right = `${b.variableName}:${b.aggregation}`;
    return left.localeCompare(right);
  });
  const contentKey = content ? JSON.stringify(content) : '';
  return `${sessionId}:${JSON.stringify(normalized)}:${contentKey}`;
}

const inflightQueries = new Map<string, Promise<SharedVariablesQueryResults>>();

/**
 * Deduplicates identical in-flight shared-variable batch queries within a scene render.
 */
export function fetchSharedVariablesWithDedup(
  sessionId: string,
  queries: SharedDataQuerySpec[],
  fetcher: () => Promise<SharedVariablesQueryResults>,
  content?: RichTextElement[]
): Promise<SharedVariablesQueryResults> {
  const key = buildCacheKey(sessionId, queries, content);
  const existing = inflightQueries.get(key);
  if (existing) {
    return existing;
  }

  const promise = fetcher().finally(() => {
    inflightQueries.delete(key);
  });
  inflightQueries.set(key, promise);
  return promise;
}

/** Clears in-flight cache (for tests). */
export function clearSharedVariablesQueryCache(): void {
  inflightQueries.clear();
}
